import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { financesAPI, standaloneAlterationsAPI, cashVouchersAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  IndianRupee, TrendingUp, Clock, AlertTriangle, ArrowUpRight, Banknote, Scissors,
} from 'lucide-react';
import ProfitabilitySection from '@/components/ProfitabilitySection';
import { useAuth } from '@/lib/auth';

const CURRENCY_SYMBOLS = { INR: '\u20B9', AED: 'AED ', USD: '$', GBP: '\u00A3' };
const METHOD_LABELS = {
  cash: 'Cash', upi: 'UPI', bank_transfer: 'Bank Transfer',
  card: 'Card', international_wire: 'Intl Wire', cheque: 'Cheque',
};
const STATUS_LABELS = {
  draft: 'Draft', confirmed: 'Confirmed', fabric_ordered: 'Fabric Ordered',
  fabric_received: 'Fabric Received', in_production: 'In Production',
  ready_for_trial: 'Ready for Trial', alterations_in_progress: 'Alterations',
  ready_for_delivery: 'Ready for Delivery', delivered: 'Delivered', cancelled: 'Cancelled',
};

function StatCard({ label, value, icon: Icon, iconColor, valueColor }) {
  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: 'var(--ink-muted)' }}>{label}</p>
            <p className="text-2xl font-bold mt-1 tabular-nums" style={{ color: valueColor || 'var(--brand-navy)' }} data-testid={`finance-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
              {value}
            </p>
          </div>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: iconColor + '12' }}>
            <Icon className="w-5 h-5" style={{ color: iconColor }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RevenueChart({ data }) {
  if (!data || data.length === 0) return null;
  const maxRevenue = Math.max(...data.map(d => d.revenue), 1);

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]" data-testid="revenue-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Monthly Revenue (Last 6 Months)</CardTitle>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="flex items-end gap-3 h-[200px] px-2">
          {data.map((d, i) => {
            const barHeight = maxRevenue > 0 ? Math.max((d.revenue / maxRevenue) * 180, 4) : 4;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                <p className="text-[10px] font-semibold tabular-nums" style={{ color: d.revenue > 0 ? 'var(--brand-navy)' : 'var(--ink-muted)' }}>
                  {d.revenue > 0 ? `${'\u20B9'}${(d.revenue / 1000).toFixed(0)}k` : '-'}
                </p>
                <div
                  className="w-full rounded-t-md"
                  style={{
                    height: `${barHeight}px`,
                    backgroundColor: i === data.length - 1 ? 'var(--brand-navy)' : 'var(--brand-gold)',
                    opacity: i === data.length - 1 ? 1 : 0.7,
                    transition: 'height 500ms ease',
                  }}
                  data-testid={`revenue-bar-${d.month_short}`}
                />
                <p className="text-[10px] font-medium" style={{ color: 'var(--ink-muted)' }}>{d.month_short}</p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default function FinancesPage() {
  const { user } = useAuth();
  const [outstanding, setOutstanding] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [revenueMonth, setRevenueMonth] = useState(null);
  const [expectedIncoming, setExpectedIncoming] = useState([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [loading, setLoading] = useState(true);

  const [altRevenue, setAltRevenue] = useState(null);
  const [pettyCash, setPettyCash] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [outRes, recRes, revRes, expRes, chartRes, altRes, pettyRes] = await Promise.all([
          financesAPI.outstanding(),
          financesAPI.recentPayments(),
          financesAPI.revenueThisMonth(),
          financesAPI.expectedIncoming(),
          financesAPI.monthlyRevenue(),
          standaloneAlterationsAPI.revenueSummary().catch(() => ({ data: null })),
          cashVouchersAPI.thisMonthTotal().catch(() => ({ data: null })),
        ]);
        setOutstanding(outRes.data);
        setRecentPayments(recRes.data);
        setRevenueMonth(revRes.data);
        setExpectedIncoming(expRes.data);
        setMonthlyRevenue(chartRes.data);
        setAltRevenue(altRes.data);
        setPettyCash(pettyRes.data);
      } catch (err) {
        console.error('Failed to fetch finances', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const totalOutstanding = outstanding.reduce((s, o) => s + o.balance_due, 0);
  const totalExpected = expectedIncoming.reduce((s, o) => s + o.expected_amount, 0);

  return (
    <div className="space-y-6" data-testid="finances-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--brand-navy)' }}>Finances</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>Payment tracking and revenue overview</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Revenue This Month"
          value={`\u20B9${(revenueMonth?.revenue || 0).toLocaleString('en-IN')}`}
          icon={TrendingUp}
          iconColor="#059669"
          valueColor="var(--success)"
        />
        <StatCard
          label="Alteration Services Revenue"
          value={`\u20B9${(altRevenue?.this_month?.revenue || 0).toLocaleString('en-IN')}`}
          icon={Scissors}
          iconColor="#7C3AED"
          valueColor="#7C3AED"
        />
        <StatCard
          label="Outstanding Balances"
          value={`\u20B9${totalOutstanding.toLocaleString('en-IN')}`}
          icon={AlertTriangle}
          iconColor="#D97706"
          valueColor="var(--warning)"
        />
        <StatCard
          label="Expected Incoming"
          value={`\u20B9${totalExpected.toLocaleString('en-IN')}`}
          icon={ArrowUpRight}
          iconColor="#2563EB"
          valueColor="#2563EB"
        />
        <StatCard
          label="Petty Cash This Month"
          value={`\u20B9${(pettyCash?.approved_total || 0).toLocaleString('en-IN')}`}
          icon={Banknote}
          iconColor="#DC2626"
          valueColor="#DC2626"
        />
      </div>

      {/* Petty Cash Info Card */}
      {pettyCash && (pettyCash.pending_count > 0 || pettyCash.approved_total > 0) && (
        <Card className="rounded-xl border bg-white" data-testid="petty-cash-summary">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(220,38,38,0.08)' }}>
                  <Banknote className="w-5 h-5" style={{ color: '#DC2626' }} />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Petty Cash Expenses ({pettyCash.month})</p>
                  <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                    {pettyCash.approved_count} approved voucher{pettyCash.approved_count !== 1 ? 's' : ''} totalling ₹{pettyCash.approved_total.toLocaleString('en-IN')}
                    {pettyCash.pending_count > 0 && (
                      <span className="ml-2 text-amber-600">• {pettyCash.pending_count} pending approval (₹{pettyCash.pending_total.toLocaleString('en-IN')})</span>
                    )}
                  </p>
                </div>
              </div>
              <Link to="/app/finances/cash-vouchers" className="text-xs font-semibold hover:underline" style={{ color: 'var(--brand-gold)' }}>
                View All →
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revenue Chart */}
      <RevenueChart data={monthlyRevenue} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Outstanding Balances */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]" data-testid="outstanding-balances-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
              <AlertTriangle className="w-4 h-4" style={{ color: 'var(--warning)' }} /> Outstanding Balances
              <Badge variant="outline" className="text-[10px] ml-auto">{outstanding.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {outstanding.length === 0 ? (
              <p className="text-sm py-6 text-center" style={{ color: 'var(--ink-muted)' }}>All payments are up to date</p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                {outstanding.map((o) => (
                  <Link key={o.order_id} to={`/app/orders/${o.order_id}`}
                    className="flex items-center justify-between py-2.5 px-3 rounded-lg border hover:bg-[color:var(--surface-2)]"
                    style={{ borderColor: 'rgba(27,42,74,0.06)', transition: 'background-color 120ms' }}
                    data-testid={`outstanding-${o.order_id}`}>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>{o.order_number}</p>
                      <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                        {o.client_name} &middot; {o.order_date}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold tabular-nums" style={{ color: 'var(--warning)' }}>
                        {CURRENCY_SYMBOLS[o.currency] || ''}{o.balance_due?.toLocaleString('en-IN')}
                      </p>
                      <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                        of {CURRENCY_SYMBOLS[o.currency] || ''}{o.total_price?.toLocaleString('en-IN')}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Expected Incoming */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]" data-testid="expected-incoming-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
              <ArrowUpRight className="w-4 h-4" style={{ color: '#2563EB' }} /> Expected Incoming
              <Badge variant="outline" className="text-[10px] ml-auto">{expectedIncoming.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {expectedIncoming.length === 0 ? (
              <p className="text-sm py-6 text-center" style={{ color: 'var(--ink-muted)' }}>No upcoming payments expected</p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                {expectedIncoming.map((o) => (
                  <Link key={o.order_id} to={`/app/orders/${o.order_id}`}
                    className="flex items-center justify-between py-2.5 px-3 rounded-lg border hover:bg-[color:var(--surface-2)]"
                    style={{ borderColor: 'rgba(27,42,74,0.06)', transition: 'background-color 120ms' }}
                    data-testid={`expected-${o.order_id}`}>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>{o.order_number}</p>
                      <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                        {o.client_name} &middot; {STATUS_LABELS[o.status] || o.status}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold tabular-nums" style={{ color: '#2563EB' }}>
                        {CURRENCY_SYMBOLS[o.currency] || ''}{o.expected_amount?.toLocaleString('en-IN')}
                      </p>
                      {o.required_by_date && (
                        <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                          Due: {o.required_by_date}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Payments */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_24px_rgba(16,24,40,0.06)]" data-testid="recent-payments-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <Banknote className="w-4 h-4" style={{ color: 'var(--success)' }} /> Recent Payments
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentPayments.length === 0 ? (
            <p className="text-sm py-6 text-center" style={{ color: 'var(--ink-muted)' }}>No payments recorded yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="recent-payments-table">
                <thead>
                  <tr className="border-b" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
                    {['Date', 'Order', 'Client', 'Amount', 'Method', 'Type', 'Recorded By'].map(h => (
                      <th key={h} className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentPayments.map((p) => (
                    <tr key={p.id} className="border-b hover:bg-[color:var(--surface-2)]" style={{ borderColor: 'rgba(27,42,74,0.04)', transition: 'background-color 120ms' }}>
                      <td className="px-3 py-2.5 text-sm" style={{ color: 'var(--navy-ink)' }}>{p.payment_date}</td>
                      <td className="px-3 py-2.5">
                        <Link to={`/app/orders/${p.order_id}`} className="text-sm font-semibold hover:underline" style={{ color: 'var(--brand-navy)' }}>
                          {p.order_number}
                        </Link>
                      </td>
                      <td className="px-3 py-2.5 text-sm" style={{ color: 'var(--navy-ink)' }}>{p.client_name}</td>
                      <td className="px-3 py-2.5 text-sm font-semibold tabular-nums" style={{ color: p.payment_type === 'refund' ? '#DC2626' : 'var(--success)' }}>
                        {p.payment_type === 'refund' ? '-' : ''}{CURRENCY_SYMBOLS[p.currency] || ''}{p.amount?.toLocaleString('en-IN')}
                      </td>
                      <td className="px-3 py-2.5 text-sm" style={{ color: 'var(--ink-muted)' }}>{METHOD_LABELS[p.payment_method] || p.payment_method}</td>
                      <td className="px-3 py-2.5">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">{p.payment_type}</Badge>
                      </td>
                      <td className="px-3 py-2.5 text-sm" style={{ color: 'var(--ink-muted)' }}>{p.recorded_by_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Profitability Section (Admin Only) */}
      {user?.role === 'Admin' && <ProfitabilitySection />}
    </div>
  );
}
